<script setup>
import { ref, computed, onMounted } from 'vue'
import { modal } from '@/common/dialog'
import TextBox from '@/components/TextBox.vue'
import Button from '@/components/Button.vue'
import Modal from '@/components/Flowbite/Modal.vue'

const props = defineProps({
  type: {
    type: String,
    default: "info" //info, question, warning, danger, question-danger, success
  },
  title: {
    type: String,
    default: null
  },
  text: {
    type: String,
    default: null
  },
  html: {
    type: String,
    default: null
  },
  persistent: { //Persistent keeps the window open until the button is clicked; otherwise user can click outside the window to close it
    type: Boolean,
    default: false
  },
  prompt: { //Prompt for text input
    type: Boolean,
    default: false
  },
  confirmButtonText: {
    type: String,
    default: "OK"
  },
  cancelButtonText: {
    type: String,
    default: null
  },
  emitter: {
    type: Object,
    default: null
  },
  _id: {
    type: String,
    default: null
  }
});

const keyfocus = ref(null);
const promptElement = ref(null);
const promptText = ref("");
const promptPlaceholder = ref("");

function htmlEncode(str) {
  return String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

const messageHtml = computed(() => {
  if (props.html) {
    return props.html;
  }

  let html = htmlEncode(props.text || "");
  html = html.replace(/\r?\n/g, "<br />");
  return html;
});

function outside() {
  if (!props.persistent) {
    cancel();
  }
}

function close(result) {
  let app = modal[props._id];
  app.unmount();

  if (props.prompt && result) {
    result.text = promptText.value;
  }

  if (props.emitter) {
    props.emitter.emit("result", result);
  }

  if (props.emitter) {
    props.emitter.emit("closing");
  }
}

function cancel() {
  close({ cancel: true });
}

function confirm() {
  close({ confirm: true });
}

defineExpose({ close, cancel, confirm });

onMounted(() => {
  if (props.prompt) {
    //Focus on the text input field
    promptElement.value.focus();
  }
  else {
    //Dummy input to handle keyboard events, i.e. Enter and Esc key
    keyfocus.value.focus();
  }
});
</script>

<template>
  <Modal size="xl" :persistent="persistent" @close="cancel" @click:outside="outside">
    <template #body>
      <!-- min-h-48 -->
      <p class="text-base leading-relaxed text-gray-500 dark:text-gray-400 grid place-items-center text-center">
        <!-- <svg class="mx-auto mb-4 text-red-700 w-24 h-24 dark:text-gray-200" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 20" v-if="type == 'danger'">
          <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 11V6m0 8h.01M19 10a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"/>
        </svg> -->
        <!-- <i class="fa fa-circle-exclamation text-red-700" style="font-size: 92px"></i> -->
        <i class="fa fa-regular fa-circle-check text-green-500 mx-auto mb-4" style="font-size: 92px" v-if="type == 'success'"></i>
        <i class="fa fa-circle-info text-blue-700 mx-auto mb-4" style="font-size: 92px" v-if="type == 'info'"></i>
        <i class="fa fa-circle-question text-blue-700 mx-auto mb-4" style="font-size: 92px" v-if="type == 'question'"></i>
        <i class="fa fa-circle-question text-red-700 mx-auto mb-4" style="font-size: 92px" v-if="type == 'question-danger'"></i>
        <i class="fa fa-triangle-exclamation text-yellow-300 mx-auto mb-4" style="font-size: 92px" v-if="type == 'warning'"></i>
        <i class="fa fa-circle-xmark fa-regular text-red-700 mx-auto mb-4" style="font-size: 92px" v-if="type == 'danger'"></i>
        <h3 class="text-lg font-medium mt-2 mb-2" v-if="title">{{ title }}</h3>
        <!-- <span class="text-center">{{ text }}</span> -->
        <span class="text-center" v-html="messageHtml"></span>
        <input ref="keyfocus" type="text" @keypress.enter="confirm" @keypress.esc="cancel" v-show="!prompt" style="width: 0; height: 0; position: fixed; left: -1000px; top: -1000px;" />
        <!-- <TextBox ref="promptElement" v-model="promptText" class="w-full" @keypress.enter="confirm" @keypress.esc="cancel" v-show="prompt" /> -->
        <input type="text" ref="promptElement" v-model="promptText" :placeholder="promptPlaceholder" role="presentation" autocomplete="prompt-modal-window"
          class="w-full bg-white border border-gray-300 text-gray-900 text-sm rounded-lg block p-2.5 focus:ring-blue-500 focus:border-blue-500"
          @keypress.enter="confirm" @keypress.esc="cancel" v-show="prompt" />
      </p>
    </template>
    <template #footer>
      <div class="flex justify-center gap-4">
        <Button type="secondary" @click="cancel" v-if="cancelButtonText">{{ cancelButtonText }}</Button>
        <Button :type="type" @click="confirm">{{ confirmButtonText }}</Button>
      </div>
    </template>
  </Modal>
</template>

<style scoped>
</style>